import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { formatINR } from '../lib/format';
import { currentFY, listRecentFYs, fyRange, isoToFY, toCSV, downloadCSV } from '../lib/csv';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

export default function Reports() {
  const [d, setD] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [fy, setFy] = useState('all');
  useEffect(() => { api.get('/reports/summary').then(r => setD(r.data)); api.get('/invoices').then(r => setInvoices(r.data.filter(x => !x.deleted_at))); }, []);
  if (!d) return <div className="p-6 text-sm text-[var(--muted)]">Loading…</div>;

  const inFY = (iso) => {
    if (fy === 'all') return true;
    const r = fyRange(fy);
    if (!r || !iso) return true;
    return iso >= r.start && iso <= r.end;
  };
  const monthsFiltered = Object.entries(d.by_month).filter(([m]) => inFY(m + '-15')).sort();
  const invFiltered = invoices.filter(i => inFY(i.issued_on || i.event_date || ''));

  const totalBooked = monthsFiltered.reduce((s, [_, v]) => s + v.booked, 0);
  const totalCollected = monthsFiltered.reduce((s, [_, v]) => s + v.collected, 0);
  const totalVGA = monthsFiltered.reduce((s, [_, v]) => s + v.value_given_away, 0);
  const totalExp = fy === 'all' ? Object.values(d.expenses_by_category).reduce((s, v) => s + v, 0) : 0; // FY-scoped expense requires backend split; simplified

  const exportInvoicesCSV = () => {
    const cols = [
      { key: 'number', label: 'Invoice #' }, { key: 'client_name', label: 'Client' },
      { key: 'event_date', label: 'Event Date' }, { key: 'issued_on', label: 'Issued' },
      { key: 'total', label: 'Total (INR)' }, { key: 'advance_received', label: 'Advance' },
      { key: 'balance', label: 'Balance' }, { key: 'status', label: 'Status' },
      { label: 'FY', get: x => isoToFY(x.issued_on || x.event_date) || '' },
    ];
    downloadCSV(`report-invoices-${fy}-${new Date().toISOString().slice(0,10)}.csv`, toCSV(invFiltered, cols));
    toast.success(`Exported ${invFiltered.length} rows`);
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div><div className="eyebrow">Analytics</div><h1 className="serif text-[28px]">Reports</h1></div>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={fy} onChange={e => setFy(e.target.value)} className="w-40" data-testid="reports-fy">
            <option value="all">All time</option>
            {listRecentFYs(6).map(f => <option key={f} value={f}>{f}{f === currentFY() ? ' (current)' : ''}</option>)}
          </select>
          <button onClick={exportInvoicesCSV} className="btn-ghost text-xs" data-testid="reports-export"><Download size={12}/> Export CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="card-cream p-4"><div className="eyebrow">Booked</div><div className="serif text-[22px] mono-num">{formatINR(totalBooked)}</div></div>
        <div className="card-cream p-4"><div className="eyebrow">Collected</div><div className="serif text-[22px] mono-num" style={{color:'var(--ok)'}}>{formatINR(totalCollected)}</div></div>
        <div className="card-cream p-4"><div className="eyebrow">Value Given</div><div className="serif text-[22px] mono-num" style={{color:'var(--muted)'}}>{formatINR(totalVGA)}</div></div>
        <div className="card-cream p-4"><div className="eyebrow">Net (collected − exp)</div><div className="serif text-[22px] mono-num" style={{color: (totalCollected - totalExp) >= 0?'var(--ok)':'var(--overdue)'}}>{formatINR(totalCollected - totalExp)}</div></div>
      </div>

      <div className="card-cream p-5 mb-4">
        <div className="eyebrow mb-3">By Month {fy !== 'all' && `— ${fy}`}</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--muted)] text-xs uppercase tracking-[0.14em]"><th className="py-1">Month</th><th className="text-right">Booked</th><th className="text-right">Collected</th><th className="text-right">Value given</th></tr></thead>
          <tbody>{monthsFiltered.map(([m,v]) => (
            <tr key={m} className="border-t hairline"><td className="py-2">{m}</td><td className="text-right mono-num">{formatINR(v.booked)}</td><td className="text-right mono-num" style={{color:'var(--ok)'}}>{formatINR(v.collected)}</td><td className="text-right mono-num text-[var(--muted)]">{formatINR(v.value_given_away)}</td></tr>
          ))}
          {monthsFiltered.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-[var(--muted)] italic">No activity in this financial year.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-cream p-5">
          <div className="eyebrow mb-3">Enquiry Sources</div>
          {Object.entries(d.sources).sort((a,b) => b[1].total - a[1].total).map(([src, v]) => (
            <div key={src} className="flex justify-between py-2 text-sm border-b hairline last:border-0"><span className="capitalize">{src}</span><span><b>{v.booked}/{v.total}</b> <span className="text-[var(--muted)] text-xs">· {v.conversion}%</span></span></div>
          ))}
          {Object.keys(d.sources).length === 0 && <div className="text-sm text-[var(--muted)] italic">No data yet.</div>}
        </div>
        <div className="card-cream p-5">
          <div className="eyebrow mb-3">Value Given Away — by reason</div>
          {Object.entries(d.value_given_away_by_reason).length === 0 && <div className="text-sm text-[var(--muted)] italic">Nothing yet.</div>}
          {Object.entries(d.value_given_away_by_reason).sort((a,b) => b[1] - a[1]).map(([r, v]) => (
            <div key={r} className="flex justify-between py-2 text-sm border-b hairline last:border-0"><span className="capitalize">{r.replace('_',' ')}</span><span className="mono-num">{formatINR(v)}</span></div>
          ))}
        </div>
        <div className="card-cream p-5 md:col-span-2">
          <div className="eyebrow mb-3">Expenses by category (all time)</div>
          {Object.entries(d.expenses_by_category).sort((a,b) => b[1] - a[1]).map(([c, v]) => (
            <div key={c} className="flex justify-between py-2 text-sm border-b hairline last:border-0"><span className="capitalize">{c.replace('_',' ')}</span><span className="mono-num">{formatINR(v)}</span></div>
          ))}
          {Object.keys(d.expenses_by_category).length === 0 && <div className="text-sm text-[var(--muted)] italic">No expenses recorded.</div>}
        </div>
      </div>
    </div>
  );
}
